const DEFAULT_PRIMARY_BASE_URL = 'https://www.vanishingincmagic.com'
const DEFAULT_UK_BASE_URL = 'https://www.vanishingincmagic.co.uk'
const DEFAULT_DEV_PRIMARY_BASE_URL = 'http://localhost:3000'
const DEFAULT_DEV_UK_BASE_URL = 'http://localhost:3001'

export type RegionalBrandCode = 'VANISHING_INC_US' | 'VANISHING_INC_UK'

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

function normalizePath(path: string): string {
  if (!path) return '/'

  const rawPath = path.startsWith('http')
    ? (() => {
        try {
          return new URL(path).pathname
        } catch {
          return path
        }
      })()
    : path

  const withLeadingSlash = rawPath.startsWith('/') ? rawPath : `/${rawPath}`
  return withLeadingSlash === '/'
    ? '/'
    : withLeadingSlash.replace(/\/+$/, match => (match ? '/' : ''))
}

function buildAbsoluteUrl(baseUrl: string, path: string): string {
  return `${stripTrailingSlash(baseUrl)}${normalizePath(path)}`
}

function inferUkBaseUrl(primaryBaseUrl: string): string {
  if (primaryBaseUrl.includes('.co.uk')) return primaryBaseUrl
  if (primaryBaseUrl.includes('.com')) {
    return primaryBaseUrl.replace(/\.com(?=\/|$)/, '.co.uk')
  }
  return DEFAULT_UK_BASE_URL
}

export function getPrimaryBaseUrl(): string {
  if (process.env.NEXT_PUBLIC_BASE_URL) {
    return stripTrailingSlash(process.env.NEXT_PUBLIC_BASE_URL)
  }

  return stripTrailingSlash(
    process.env.NODE_ENV === 'production'
      ? DEFAULT_PRIMARY_BASE_URL
      : DEFAULT_DEV_PRIMARY_BASE_URL
  )
}

export function getUkBaseUrl(): string {
  const configured = process.env.NEXT_PUBLIC_BASE_URL_UK
  if (configured) return stripTrailingSlash(configured)

  if (process.env.NODE_ENV !== 'production') {
    return stripTrailingSlash(DEFAULT_DEV_UK_BASE_URL)
  }

  return stripTrailingSlash(inferUkBaseUrl(getPrimaryBaseUrl()))
}

export function getBaseUrlForBrand(brandCode: RegionalBrandCode): string {
  return brandCode === 'VANISHING_INC_UK' ? getUkBaseUrl() : getPrimaryBaseUrl()
}

export function normalizeComparableHost(host: string): string {
  return host.trim().toLowerCase()
}

function stripPort(host: string): string {
  return host.replace(/:\d+$/, '')
}

function isLocalDevHost(host: string): boolean {
  return host.startsWith('localhost') || host.startsWith('127.0.0.1')
}

function isLocalUkOverrideHost(host: string): boolean {
  return isLocalDevHost(host) && host.includes(':3001')
}

/**
 * Classify request/browser host into the storefront market.
 * Production rule: `.co.uk` => UK, everything else => US.
 * Development override: localhost/127.0.0.1:3001 => UK.
 */
export function inferRegionalBrandFromHostname(host: string): RegionalBrandCode | null {
  const normalized = normalizeComparableHost(host)
  if (!normalized) return null

  if (isLocalDevHost(normalized)) {
    if (isLocalUkOverrideHost(normalized)) return 'VANISHING_INC_UK'
    return 'VANISHING_INC_US'
  }

  const hostname = stripPort(normalized)
  return hostname.endsWith('.co.uk') ? 'VANISHING_INC_UK' : 'VANISHING_INC_US'
}

export function getBrandCodeForHost(host: string): RegionalBrandCode | null {
  return inferRegionalBrandFromHostname(host)
}

export function getCurrencyCodeForHost(host: string): 'GBP' | 'USD' {
  const brandCode = getBrandCodeForHost(host)
  return brandCode === 'VANISHING_INC_UK' ? 'GBP' : 'USD'
}

export function getCurrencySymbolForHost(host: string): '£' | '$' {
  return getCurrencyCodeForHost(host) === 'GBP' ? '£' : '$'
}

export type MarketBrandCode = 'VANISHING_INC_US' | 'VANISHING_INC_UK'

/** Base URL for the storefront that should appear in canonicals/OG/JSON-LD for this brand. */
export function getSiteBaseUrlForBrand(brandCode: MarketBrandCode): string {
  return brandCode === 'VANISHING_INC_UK'
    ? getUkBaseUrl()
    : getPrimaryBaseUrl()
}

export function getAbsoluteUrlForBrand(
  path: string,
  brandCode: MarketBrandCode
): string {
  return buildAbsoluteUrl(getSiteBaseUrlForBrand(brandCode), path)
}

export function getCanonicalUrl(path: string): string {
  return buildAbsoluteUrl(getPrimaryBaseUrl(), path)
}

export function getCanonicalUrlForBrand(
  path: string,
  brandCode: MarketBrandCode
): string {
  return buildAbsoluteUrl(getSiteBaseUrlForBrand(brandCode), path)
}

export function getRegionalUrls(path: string): {
  us: string
  uk: string
  xDefault: string
} {
  const normalizedPath = normalizePath(path)
  const us = buildAbsoluteUrl(getPrimaryBaseUrl(), normalizedPath)
  const uk = buildAbsoluteUrl(getUkBaseUrl(), normalizedPath)

  return {
    us,
    uk,
    xDefault: us,
  }
}
